import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "@emotion/react/jsx-runtime";
import { useEffect, useState } from 'react';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from "react-router-dom";
import useWidgetStore from '../store/useWidgetStore';
import useDataStore from '../store/useDataStore';
import dayjs from 'dayjs';
import 'dayjs/locale/nl';
import 'dayjs/locale/en';
import 'dayjs/locale/fr';
import 'dayjs/locale/de';
import { useI18N } from '../context/I18NContext';
import { useFlow } from '../context/FlowContext';
import { useSettings } from '../context/SettingsContext';
export default function BookingSummary() {
    const navigate = useNavigate();
    const [apptypeStr, setApptypeStr] = useState();
    const [resourceStr, setResourceStr] = useState();
    const [capacityStr, setCapacityStr] = useState();
    const [totalPriceStr, setTotalPriceStr] = useState();
    const [date, setDate] = useState();
    const { t, ts } = useI18N();
    const theme = useTheme();
    const flow = useFlow();
    const { showSelectedResource, staticData } = useSettings();
    const { booking, cart, enabled, language, selectedCapacity, selectedCapacityGroups, setEnabled } = useWidgetStore();
    const { apptypes, resources } = useDataStore();
    useEffect(() => {
        setEnabled(Object.assign(Object.assign({}, enabled), { apptype: true, resource: true }));
    }, []);
    const [hasAppointmentProducts, setHasAppointmentProducts] = useState(false);
    useEffect(() => {
        setHasAppointmentProducts(cart.lines.filter((line) => line.origin === 'appointment').length > 0);
    }, [cart.lines]);
    useEffect(() => {
        var _a;
        if (booking.appointments) {
            let appointment = booking.appointments[0];
            setDate(dayjs(appointment.date).locale(language).format('dddd D MMMM HH:mm'));
            let component = appointment.components[0];
            let _apptypes = [];
            let _resources = [];
            component.resources.forEach((resource_id) => {
                let resource = staticData === null || staticData === void 0 ? void 0 : staticData.resources.find((resource) => {
                    return resource.id === resource_id;
                });
                if (resource) {
                    _resources.push(resource.name);
                }
            });
            component.appointmentTypes.forEach((appointmentType_id) => {
                let appointmentType = apptypes.find((appointmentType) => appointmentType.id === appointmentType_id);
                if (appointmentType) {
                    _apptypes.push(appointmentType.name);
                }
            });
            setApptypeStr(_apptypes.join(', '));
            setResourceStr(_resources.join(', '));
            if (selectedCapacity > 1) {
                if (selectedCapacityGroups[0]) {
                    setCapacityStr(ts('%capacity% personen', { capacity: selectedCapacity.toString() }, 'summary'));
                }
                else {
                    let parts = [];
                    const view = flow.views.find((view) => {
                        var _a;
                        const groups = (_a = view === null || view === void 0 ? void 0 : view.components) === null || _a === void 0 ? void 0 : _a.find((component) => {
                            if (component.picker === 'capacity') {
                                return component.config.groups;
                            }
                            return undefined;
                        });
                        return groups === null || groups === void 0 ? void 0 : groups.config.groups;
                    });
                    const groups = (_a = view === null || view === void 0 ? void 0 : view.components) === null || _a === void 0 ? void 0 : _a.find((component) => {
                        if (component.picker === 'capacity') {
                            return component.config.groups;
                        }
                        return undefined;
                    });
                    for (const [_key, _value] of Object.entries(selectedCapacityGroups)) {
                        if (_key !== '0' && _value > 0) {
                            let groupName = groups === null || groups === void 0 ? void 0 : groups.config.groups.find((group) => group.id === parseInt(_key)).name;
                            parts.push(ts('%capacity% %groupName%', { capacity: _value, groupName: t(groupName, 'select') }, 'summary'));
                        }
                    }
                    setCapacityStr(parts.join(', '));
                }
            }
            else {
                setCapacityStr('');
            }
            if (appointment.price > 0) {
                let price = new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(appointment.price).replace(',00', ',-');
                setTotalPriceStr(ts('Prijs: %price%', { price: price }, 'summary'));
            }
            else {
                setTotalPriceStr('');
            }
        }
    }, [apptypes, resources, selectedCapacityGroups, selectedCapacity]);
    const goTo = (e) => {
        let target = e.target.closest('button');
        let path = target.getAttribute('rel');
        navigate(`/oa_widget_${path}`);
    };
    // console.log(cart.lines)
    return (_jsx(_Fragment, { children: hasAppointmentProducts && (_jsxs(Stack, { direction: 'row', justifyContent: "space-between", alignItems: "center", spacing: 2, sx: {
                background: theme.palette.background.default,
                color: theme.palette.text.primary,
                borderColor: theme.palette.divider,
                borderWidth: '0 0 1px 0',
                borderStyle: "solid",
                position: 'relative',
                padding: 1
            }, children: [_jsxs(Box, { sx: { paddingTop: 1 }, children: [_jsxs(Typography, { variant: "caption", sx: {
                                fontWeight: 500,
                                fontSize: '0.9rem'
                            }, children: [t('Je huidige selectie', 'nav'), ":"] }), _jsx(Typography, { variant: "subtitle2", sx: {
                                fontWeight: 700,
                                fontSize: '1.1rem',
                            }, children: date }), apptypeStr !== '' && (_jsx(Typography, { variant: "subtitle2", sx: {
                                fontWeight: 500,
                                fontSize: '1.1rem',
                            }, children: apptypeStr })), showSelectedResource && resourceStr !== '' && (_jsx(Typography, { variant: "subtitle2", sx: {
                                fontWeight: 500,
                                fontSize: '1.1rem',
                            }, children: resourceStr })), capacityStr !== '' && (_jsx(Typography, { variant: "subtitle2", sx: {
                                fontWeight: 500,
                                fontSize: '1.1rem',
                            }, children: capacityStr })), totalPriceStr !== '' && (_jsx(Typography, { variant: "subtitle2", sx: {
                                fontWeight: 500,
                                fontSize: '1.1rem',
                            }, children: totalPriceStr }))] }), _jsx(Box, { sx: { flexGrow: 1, textAlign: "right", alignContent: "space-around" }, children: _jsx(Button, { onClick: goTo, rel: 'index', variant: "contained", sx: { my: 1 }, children: t('Wijzigen', 'nav') }) })] })) }));
}
